import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DynamicHttpTableComponent } from '@components/ng-dynamic-http-table';
import {
  DisplayedColumnsObject,
} from '@components/ng-dynamic-http-table/src/app/interfaces/displayed-columns-object.interface';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';

import { MainTitle } from '../../../models/main-title';
import { User } from '../../../models/user';
import { CustomSnackbarService } from '../../../services/custom-snackbar.service';
import { UserService } from '../../../services/user.service';
import { AuthService } from '../../../shared-components/ng-login/auth/auth.service';
import { displayRoles, displayStatus } from '../../../utils/display-texts';
import { Roles } from '../../../utils/enums';
import { ConfirmComponent } from '../../confirm/confirm.component';
import { ChangeRolesComponent } from './change-roles/change-roles.component';
import { InviteUserComponent } from './invite-user/invite-user.component';
import { ReactivateUserComponent } from './reactivate-user/reactivate-user.component';
import { UserToImpersonateDataModalComponent }
  from './user-to-impersonate-data-modal/user-to-impersonate-data-modal.component';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  public title = new MainTitle('UserManagementComponent.title');
  /**
   * Child table component
   */
  @ViewChild('userTable', { static: false }) userTable?: DynamicHttpTableComponent;
  actionMapping = {
    invite: (user: User): void => this.resendInvitation(user),
    delete: (user: User): void => this.deleteUser(user),
    impersonate: (user: User): void => this.openImpersonateModal(user),
    reactivate: (user: User): void => this.reactivateUser(user),
    changeRoles: (user: User): void => this.openChangeRolesDialog(user.email, user.roles)
  };

  searchInput: FormGroup;
  myColumns: DisplayedColumnsObject = {
    email: { label: 'Correo', type: 'text' },
    roles: { label: 'Rol', filter: displayRoles, type: 'text' },
    enabled: {
      label: 'Estado',
      filter: (enabled: boolean, user: User): string => displayStatus(enabled, user),
      type: 'text'
    },
    menu: {
      label: '',
      type: 'menu-button',
      elements: [
        {
          label: 'Enviar invitación',
          method: (el: User): void => this.actionMapping['invite'](el),
          show: (el: User): boolean => this.isCurrentUserAdmin() && el.deleted !== true
        },
        {
          label: 'Reactivar usuario',
          method: (el: User): void => this.actionMapping['reactivate'](el),
          show: (el: User): boolean => el.deleted === true
        },
        {
          label: 'Borrar usuario',
          method: (el: User): void => this.actionMapping['delete'](el),
          show: (el: User): boolean => this.isCurrentUserAdmin() && el.deleted !== true
        },
        {
          label: 'Impersonar usuario',
          method: (el: User): void => this.actionMapping['impersonate'](el),
          show: (el: User): boolean => el.deleted !== true
        },
        {
          label: 'Cambiar roles',
          method: (el: User): void => this.actionMapping['changeRoles'](el),
          show: (el: User): boolean => this.isCurrentUserAdmin() && el.deleted !== true
        }
      ],
      class: 'fa-solid fa-ellipsis-vertical',
      buttonClass: 'btn-charges'
    }
  };

  constructor(
    public userService: UserService,
    private builder: FormBuilder,
    public dialogService: MatDialog,
    private snackBarService: CustomSnackbarService,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    this.searchInput = this.builder.group({
      searchText: ''
    });
  }

  ngOnInit(): void {
    this.searchInput.valueChanges.pipe(debounceTime(400)).subscribe(() => this.search());
  }

  openImpersonateModal(user: User): void {
    this.dialogService.open(UserToImpersonateDataModalComponent, {
      width: '33.6rem',
      data: {
        email: user.email
      }
    });
  }

  /**
   * Method to search by query and reload the table
   */
  search(): void {
    this.userTable?.applyFilter(this.searchInput.get('searchText')?.value);
  }

  /**
   * Method to resend email to the user to activate the account
   * @param user
   */
  resendInvitation(user: User): void {
    this.userService.resendRestorePassword(user).subscribe(() => {
      this.snackBarService.present('An email has been sent to the user to activate the account.');
    });
  }

  openReactivateUserDialog(): void {
    this.dialogService.open(ReactivateUserComponent, {
      panelClass: 'noo-padding-panel',
      width: '538px',
      height: '250px'
    });
  }

  /**
   * Method to reactivate a user
   * @param user
   */
  reactivateUser(user: User): void {
    this.userService.reactivateUser(user.email).subscribe(() => {
      this.snackBarService.present(this.translate.instant('ReactivateUserComponent.reactivatedUser'));
      user.deleted = false;
    });
  }


  openChangeRolesDialog(userEmail: string, currentRoles: string[]): void {
    this.dialogService.open(ChangeRolesComponent, {
      width: '538px',
      data: {
        userEmail: userEmail,
        currentRoles: currentRoles,
        userTable: this.userTable
      }
    });
  }

  openInviteUserDialog(): void {
    this.dialogService.open(InviteUserComponent, {
      width: '538px'
    });
  }

  /**
   * Method to check if the current user is admin
   */
  isCurrentUserAdmin(): boolean {
    return this.authService.getUserData().roles.includes(Roles.ADMIN)
  }

  /**
   * Method to delete the user
   * @param user
   */
  deleteUser(user: User): void {
    const delDialogRef = this.dialogService.open(ConfirmComponent, {
      width: '400px',
      data: {
        acceptDialog: 'Delete user',
        title: 'Delete User',
        message: 'Are you sure you want to delete this user?',
        destructive: true
      }
    });

    delDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.userService.delete(user._id).subscribe(() => {
          this.userTable?.getResults();
        });
      }
    });
  }
}
